import {
  faBuilding,
  faCircleQuestion,
  faCity,
  faHouse,
  faHouseChimney,
  faHouseChimneyWindow,
} from '@fortawesome/pro-duotone-svg-icons'

import { QuickPassProfile } from '../../_types/quickpass-profile'
import { OnboardingStepDefinition, OnboardingStepKey } from './types'

const ONBOARDING_STEPS: OnboardingStepDefinition[] = [
  {
    key: 'name',
    title: "Let's start with your name",
    subtitle: "So your future landlords know who they're talking to.",
  },
  {
    key: 'cities',
    title: 'Where are you looking?',
    subtitle:
      "Add as many cities as you like — we'll match listings across all of them.",
  },
  {
    key: 'preferences',
    title: "What's your dream setup?",
    subtitle: 'Price, bedrooms, the whole deal. You can update these any time.',
  },
  {
    key: 'property-types',
    title: 'What kind of place feels right?',
    subtitle: "Pick everything you'd consider — we'll surface the rest.",
  },
]

const COMMON_CANADIAN_CITIES = [
  'Toronto',
  'Vancouver',
  'Montreal',
  'Calgary',
  'Ottawa',
  'Edmonton',
  'Mississauga',
  'Hamilton',
  'Winnipeg',
  'Quebec City',
  'Kitchener',
  'Waterloo',
  'London',
  'Halifax',
  'Victoria',
]

const BEDROOM_OPTIONS = [
  { value: 0, label: 'Studio' },
  { value: 1, label: '1 bed' },
  { value: 2, label: '2 beds' },
  { value: 3, label: '3 beds' },
  { value: 4, label: '4+ beds' },
]

const BATHROOM_OPTIONS = [
  { value: 1, label: '1 bath' },
  { value: 2, label: '2 baths' },
  { value: 3, label: '3+ baths' },
]

const DWELLING_SCOPE_OPTIONS = [
  {
    value: 'whole-property' as const,
    title: 'A whole place',
    description: 'I want the entire house, condo, or townhouse to myself.',
    icon: faHouse,
  },
  {
    value: 'unit' as const,
    title: 'A unit',
    description:
      'A unit in a building, basement suite, or shared structure works for me.',
    icon: faBuilding,
  },
  {
    value: 'no-preference' as const,
    title: 'Either works',
    description: "I'm open — show me everything that fits the rest.",
    icon: faCircleQuestion,
  },
]

const PROPERTY_TYPE_OPTIONS = [
  {
    value: 'apartment' as const,
    title: 'Apartment',
    description: 'Purpose-built apartments in low- or high-rise buildings.',
    icon: faBuilding,
  },
  {
    value: 'house' as const,
    title: 'House',
    description: 'Detached, semi, basement suites — single-structure rentals.',
    icon: faHouseChimney,
  },
  {
    value: 'condo' as const,
    title: 'Condo',
    description: 'Privately-owned units in condo buildings.',
    icon: faCity,
  },
  {
    value: 'townhouse' as const,
    title: 'Townhouse',
    description: 'Row houses, stacked townhomes, multi-floor units.',
    icon: faHouseChimneyWindow,
  },
]

/**
 * Picks the step the renter should land on, based on what their profile
 * already has filled in. New users start at step 0; returners continue
 * where they left off.
 *
 * @param {QuickPassProfile | null} profile
 * @returns {number}
 */
const getInitialStepIndex = (profile: QuickPassProfile | null): number => {
  if (profile === null) {
    return 0
  }

  if (profile.firstName === '' || profile.lastName === '') {
    return 0
  }

  if (profile.unitPreferences.cities.length === 0) {
    return 1
  }

  const hasPreferences =
    profile.unitPreferences.priceMax !== null ||
    profile.unitPreferences.bedroomCounts.length > 0 ||
    profile.unitPreferences.dwellingScope !== 'no-preference'

  if (hasPreferences === false) {
    return 2
  }

  if (profile.unitPreferences.propertyTypes.length === 0) {
    return 3
  }

  return ONBOARDING_STEPS.length - 1
}

const getStepByIndex = (index: number): OnboardingStepDefinition => {
  return ONBOARDING_STEPS[index] ?? ONBOARDING_STEPS[0]
}

const getStepKeyByIndex = (index: number): OnboardingStepKey => {
  return getStepByIndex(index).key
}

export {
  ONBOARDING_STEPS,
  COMMON_CANADIAN_CITIES,
  BEDROOM_OPTIONS,
  BATHROOM_OPTIONS,
  DWELLING_SCOPE_OPTIONS,
  PROPERTY_TYPE_OPTIONS,
  getInitialStepIndex,
  getStepByIndex,
  getStepKeyByIndex,
}
